/**
 * EligibilityModal.jsx — Iter402-P6
 * ----------------------------------
 * Tam-ekran eğitici modal. `guardOfferAction` ilk 3 çağrıda toast yerine bu
 * modalı showir (localStorage sayacı). Modal içinde HowItWorksDiagram +
 * spesifik hatayı vurgulayan CTA bulunur.
 *
 * Module-level mini emitter: EligibilityModal tek kez <App>'a mount edilir,
 * `showEligibilityModal(data, navigate)` fonksiyonu herhangi bir yerden
 * çağrılabilir.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, LogIn, Layers, Package, ArrowRight } from 'lucide-react';
import HowItWorksDiagram from './HowItWorksDiagram';

const LS_SEEN = 'eligibility_modal_seen_count';
const MAX_MODAL_SHOWS = 3;

// Minimal pub/sub
let openCb = null;
export function showEligibilityModal(data, navigate) {
  if (typeof openCb === 'function') openCb(data, navigate);
}

const REASON_META = {
  not_logged_in: {
    icon: LogIn,
    title: 'You must log in',
    color: 'text-blue-700',
    primary: { label: 'Log In', to: '/login' },
  },
  wrong_role: {
    icon: Package,
    title: 'A suitable role is required',
    color: 'text-amber-700',
    primary: { label: 'Activate Role', to: '/become-provider' },
  },
  no_package: {
    icon: Package,
    title: 'You have no active package',
    color: 'text-amber-700',
    primary: { label: 'Select Package', to: '/packages' },
  },
  no_subscription: {
    icon: Layers,
    title: 'Category subscription required',
    color: 'text-emerald-700',
    primary: { label: 'Go to Subscriptions', to: '/provider-dashboard?tab=subscriptions' },
  },
};

export default function EligibilityModal() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [nav, setNav] = useState(null);

  useEffect(() => {
    openCb = (payload, navigate) => {
      setData(payload);
      setNav(() => navigate);
      setOpen(true);
    };
    return () => { openCb = null; };
  }, []);

  if (!data) return null;
  const meta = REASON_META[data.reason] || REASON_META.no_package;
  const Icon = meta.icon;

  const handleCTA = () => {
    setOpen(false);
    if (nav) nav(data.redirect_to || meta.primary.to);
  };

  const handleLater = () => setOpen(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" data-testid="eligibility-modal">
        <DialogHeader>
          <div className={`flex items-center gap-3 mb-2 ${meta.color}`}>
            <Icon className="w-7 h-7" />
            <DialogTitle className="text-xl">{meta.title}</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-slate-600">
            {data.message}
          </DialogDescription>
        </DialogHeader>

        <div className="my-4">
          <HowItWorksDiagram compact />
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleLater}
            className="flex-1"
            data-testid="eligibility-modal-later-btn"
          >
            Remind Later
          </Button>
          <Button
            onClick={handleCTA}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            data-testid="eligibility-modal-cta-btn"
          >
            {meta.primary.label} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        <p className="text-xs text-slate-400 text-center mt-2 flex items-center justify-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          This guide is shown in the first 3 visits, then appears as a small notification.
        </p>
      </DialogContent>
    </Dialog>
  );
}

/** Counter helpers (read + increment); imported by offerEligibility.js */
export function shouldShowEligibilityModal() {
  const seen = parseInt(localStorage.getItem(LS_SEEN) || '0', 10);
  return seen < MAX_MODAL_SHOWS;
}

export function incrementEligibilityModalSeen() {
  const seen = parseInt(localStorage.getItem(LS_SEEN) || '0', 10);
  localStorage.setItem(LS_SEEN, String(seen + 1));
}
